/**
 * VendorQuadrant — 2x2 price-vs-performance scatter for vendors.
 *
 * Axes:
 *   X = $/1k tokens (low → high) — uses the weighted per-1k from the burn
 *       rollup when available, otherwise the static vendor.per1kToken.
 *   Y = P95 latency (fast → slow, INVERTED so up = better)
 *
 * Quadrants:
 *   Top-left:  ★ Stars        (cheap + fast)
 *   Top-right: Premium-perf   (pricey but fast)
 *   Bot-left:  Bargains       (cheap but slow)
 *   Bot-right: Re-evaluate    (expensive AND slow)
 *
 * Bubble area encodes monthly spend so the audience sees "who's biggest"
 * at a glance. Hover for tooltip; click navigates to vendor detail.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";

const PADDING = { top: 24, right: 24, bottom: 36, left: 56 };
const WIDTH = 720;
const HEIGHT = 360;
const INNER_W = WIDTH - PADDING.left - PADDING.right;
const INNER_H = HEIGHT - PADDING.top - PADDING.bottom;

function vendorColor(v) {
  // Stable colour per vendor id (matches sidebar dot accents).
  const palette = ["#0a4c90", "#c4991b", "#0e7a52", "#7a3a8c", "#b34a2e", "#365b85", "#7a661e", "#3a7a6a"];
  let h = 0;
  for (const ch of (v.id || v.name || "")) h = (h * 31 + ch.charCodeAt(0)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
}

export default function VendorQuadrant({ vendors, burnByVendor }) {
  const navigate = useNavigate();
  const [hoverId, setHoverId] = useState(null);

  const points = useMemo(() => {
    return vendors
      .map((v) => {
        const burn = burnByVendor?.get?.(v.id);
        const per1k = burn?.weightedPer1kToken ?? v.per1kToken ?? null;
        const lat = v.latencyMs;
        if (per1k == null || !lat) return null;
        return {
          id: v.id,
          name: v.name,
          per1k,
          latency: lat,
          spend: v.monthlySpendAud || 0,
          status: v.status,
          agents: v.agentsGoverned || 0,
        };
      })
      .filter(Boolean);
  }, [vendors, burnByVendor]);

  if (!points.length) {
    return (
      <div className="surface-card shadow-e1 p-6 text-center text-meta text-ink-secondary" data-testid="vendor-quadrant-empty">
        Not enough data to plot the price–performance quadrant yet.
      </div>
    );
  }

  // Scales. Pad ranges by 10% so dots aren't squished against axes.
  const xs = points.map((p) => p.per1k);
  const ys = points.map((p) => p.latency);
  const spends = points.map((p) => p.spend);
  const xMin = Math.min(...xs) * 0.9;
  const xMax = Math.max(...xs) * 1.1;
  const yMin = Math.min(...ys) * 0.9;
  const yMax = Math.max(...ys) * 1.1;
  const xMid = (xMin + xMax) / 2;
  const yMid = (yMin + yMax) / 2;
  const spendMax = Math.max(...spends, 1);

  const sx = (x) => PADDING.left + ((x - xMin) / (xMax - xMin)) * INNER_W;
  // Note Y is INVERTED: lower latency = higher Y position (= better).
  const sy = (y) => PADDING.top + ((y - yMin) / (yMax - yMin)) * INNER_H;
  // Bubble radius: scale by sqrt of spend for area-proportional sizing.
  const sr = (s) => 6 + Math.sqrt(s / spendMax) * 22;

  const fmtPer1k = (v) => `A$${v.toFixed(4)}`;
  const fmtLat = (ms) => `${(ms / 1000).toFixed(2)}s`;
  const fmtAud = (n) => `A$${n >= 1e6 ? (n / 1e6).toFixed(1) + "M" : Math.round(n / 1000) + "k"}`;

  const hover = hoverId ? points.find((p) => p.id === hoverId) : null;

  return (
    <div className="surface-card shadow-e1 overflow-hidden" data-testid="vendor-quadrant-panel">
      <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles size={13} className="text-tralenex-700" />
          <p className="eyebrow">Price–performance quadrant</p>
        </div>
        <p className="text-meta text-ink-disabled">
          X = $/1k tokens · Y = P95 latency (lower is better) · bubble = monthly spend
        </p>
      </div>

      <div className="relative px-4 py-3">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="w-full h-auto"
          data-testid="vendor-quadrant-svg"
        >
          {/* Quadrant background tints */}
          <rect x={PADDING.left} y={PADDING.top} width={INNER_W / 2} height={INNER_H / 2} fill="#0e7a52" opacity="0.05" />
          <rect x={PADDING.left + INNER_W / 2} y={PADDING.top} width={INNER_W / 2} height={INNER_H / 2} fill="#c4991b" opacity="0.06" />
          <rect x={PADDING.left} y={PADDING.top + INNER_H / 2} width={INNER_W / 2} height={INNER_H / 2} fill="#c4991b" opacity="0.04" />
          <rect x={PADDING.left + INNER_W / 2} y={PADDING.top + INNER_H / 2} width={INNER_W / 2} height={INNER_H / 2} fill="#b34a2e" opacity="0.06" />

          {/* Quadrant dividers */}
          <line x1={sx(xMid)} x2={sx(xMid)} y1={PADDING.top} y2={PADDING.top + INNER_H} stroke="#d8dee8" strokeDasharray="3 3" />
          <line x1={PADDING.left} x2={PADDING.left + INNER_W} y1={sy(yMid)} y2={sy(yMid)} stroke="#d8dee8" strokeDasharray="3 3" />

          {/* Axes */}
          <line x1={PADDING.left} x2={PADDING.left + INNER_W} y1={PADDING.top + INNER_H} y2={PADDING.top + INNER_H} stroke="#c5cdd9" strokeWidth="0.6" />
          <line x1={PADDING.left} x2={PADDING.left} y1={PADDING.top} y2={PADDING.top + INNER_H} stroke="#c5cdd9" strokeWidth="0.6" />

          {/* Axis labels */}
          <text x={PADDING.left + INNER_W / 2} y={HEIGHT - 8} textAnchor="middle" fontSize="10" fill="#4c5a75" fontWeight="600">$ / 1k tokens →</text>
          <text x={14} y={PADDING.top + INNER_H / 2} fontSize="10" fill="#4c5a75" fontWeight="600" transform={`rotate(-90 14 ${PADDING.top + INNER_H / 2})`} textAnchor="middle">← Faster P95 latency</text>

          {/* Min / max tick labels */}
          <text x={PADDING.left} y={PADDING.top + INNER_H + 14} fontSize="9" fill="#949fb5" textAnchor="start">{fmtPer1k(xMin)}</text>
          <text x={PADDING.left + INNER_W} y={PADDING.top + INNER_H + 14} fontSize="9" fill="#949fb5" textAnchor="end">{fmtPer1k(xMax)}</text>
          <text x={PADDING.left - 4} y={PADDING.top + 4} fontSize="9" fill="#949fb5" textAnchor="end">{fmtLat(yMin)}</text>
          <text x={PADDING.left - 4} y={PADDING.top + INNER_H} fontSize="9" fill="#949fb5" textAnchor="end">{fmtLat(yMax)}</text>

          {/* Quadrant labels */}
          <text x={PADDING.left + 8} y={PADDING.top + 14} fontSize="10" fontWeight="700" fill="#0e7a52" opacity="0.78">★ STARS  cheap + fast</text>
          <text x={PADDING.left + INNER_W - 8} y={PADDING.top + 14} fontSize="10" fontWeight="700" fill="#7a661e" opacity="0.78" textAnchor="end">PREMIUM-PERF  pricey + fast</text>
          <text x={PADDING.left + 8} y={PADDING.top + INNER_H - 6} fontSize="10" fontWeight="700" fill="#7a661e" opacity="0.78">BARGAIN  cheap + slow</text>
          <text x={PADDING.left + INNER_W - 8} y={PADDING.top + INNER_H - 6} fontSize="10" fontWeight="700" fill="#b34a2e" opacity="0.78" textAnchor="end">RE-EVALUATE  expensive + slow</text>

          {/* Bubbles */}
          {points.map((p) => (
            <g
              key={p.id}
              onMouseEnter={() => setHoverId(p.id)}
              onMouseLeave={() => setHoverId(null)}
              onClick={() => navigate(`/vendors/integrations#${p.id}`)}
              style={{ cursor: "pointer" }}
              data-testid={`vendor-quadrant-dot-${p.id}`}
            >
              <circle
                cx={sx(p.per1k)}
                cy={sy(p.latency)}
                r={sr(p.spend)}
                fill={vendorColor(p)}
                fillOpacity={hoverId && hoverId !== p.id ? 0.18 : 0.55}
                stroke={vendorColor(p)}
                strokeWidth={hoverId === p.id ? 2.2 : 1.4}
                style={{ transition: "all 180ms ease" }}
              />
              <text
                x={sx(p.per1k)}
                y={sy(p.latency) + sr(p.spend) + 11}
                textAnchor="middle"
                fontSize="10"
                fontWeight="700"
                fill="#0c1629"
                pointerEvents="none"
              >
                {p.name}
              </text>
            </g>
          ))}
        </svg>

        {/* Tooltip / inspector strip */}
        <div
          className="mt-2 h-[42px] flex items-center gap-4 px-3 rounded-md bg-inset/40 text-meta"
          data-testid="vendor-quadrant-tooltip"
        >
          {hover ? (
            <>
              <span
                className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: vendorColor(hover) }}
              />
              <span className="font-bold text-ink-primary">{hover.name}</span>
              <span className="text-ink-secondary"><span className="font-mono">{hover.id}</span></span>
              <span className="text-ink-secondary">·  {fmtPer1k(hover.per1k)} / 1k tok</span>
              <span className="text-ink-secondary">·  {fmtLat(hover.latency)} P95</span>
              <span className="text-ink-secondary">·  {fmtAud(hover.spend)}/mo</span>
              <span className="text-ink-secondary">·  {hover.agents} agents</span>
              <span className="ml-auto text-ink-disabled">Click bubble to open integration</span>
            </>
          ) : (
            <span className="text-ink-disabled">Hover any bubble to inspect — larger = more spend · greener = cheaper + faster.</span>
          )}
        </div>
      </div>
    </div>
  );
}
